import React, {useCallback, useEffect, useRef, useState} from 'react';

import {Analytics} from '../config/analytics/analytics.web';
import {Auth} from '../onBoarding/navigation';
import {B2BNavigator} from '@buncha/B2B/navigation/MainNavigator';
import {B2BUserNavigator} from '@buncha/B2B/navigation/UserMainNavigator';
import BottomTab from './Tabs';
import {Common} from '../common/navigation';
import DownTime from '@buncha/maintenance/screens/DownTime';
import {DriverNavigator} from '@buncha/driver/navigation';
import {HiringNavigator} from '@buncha/hiring/navigation';
import {Home} from '@buncha/home/navigation';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '../components/conditional/If';
import {Linking} from 'react-native';
import Lockout from '../onBoarding/screens/Lockout';
import {MainScreens} from './types';
import {NativeBaseProvider} from 'native-base';
import OrientationWarning from '@buncha/B2B/components/OrientationWarning/OrientationWarning';
import {ShopperNavigator} from '@buncha/home/navigation/MainNavigator';
import Splash from '@buncha/onBoarding/screens/Splash';
import {StorageKeys} from '@buncha/config/storage/type';
import {Toast} from '../components/composites';
import {WebConfig} from '@buncha/utils/types';
import {createNativeStackNavigator} from '@react-navigation/native-stack';
import {getConfig} from '@buncha/utils/appInfo';
import {getScreenToNavigate} from './utils/navigation';
import {gqlService} from '../config/graphQL';
import {storage} from '@buncha/config/storage';
import {useAppSelector} from '../config/redux/hooks';
import {useFetchDownTimeInfo} from '@buncha/maintenance/hooks/downtime';
import {useFetchLocalizationData} from '@buncha/localization/hooks';
import {useIntercom} from 'react-use-intercom';

const Stack = createNativeStackNavigator();

const MainNavigator: React.FC = () => {
  const onboardingState = useAppSelector(gstate => gstate.onboarding);
  const authData = onboardingState.userAuthData;
  const whenIWorkToken = onboardingState.userAuthData?.wToken;
  const notificationToastInfo = onboardingState.notificationToastInfo;

  const intercom = useIntercom();

  useEffect(() => {
    Analytics.setupForServerConfiguration(intercom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAppUser = useCallback(async () => {
    const urlData = (await Linking.getInitialURL()) || '';
    const user: string[] = urlData?.split('userId=');
    const hasUser = user.length > 1;

    const userId = hasUser
      ? user[1].split('?tn')[0]
      : await storage.getItem(StorageKeys.user);
    return userId;
  }, []);

  const manageLoginToken = useCallback(async () => {
    const urlData = (await Linking.getInitialURL()) || '';
    const token: string[] = urlData.split('tn=');
    const userId = await getAppUser();
    gqlService?.setUser(String(userId));
    let loginToken = (await storage.getItem(StorageKeys.loginToken)) || '';
    const hasTokenParam = token?.length > 1 && token[1].length;
    if (hasTokenParam) loginToken = token[1];
    gqlService?.setLoginCredential(loginToken);
  }, [getAppUser]);

  useEffect(() => {
    manageLoginToken();
    gqlService?.setLoginCredential(authData?.loginToken);
    if (whenIWorkToken?.length)
      gqlService?.setWhenIWorkLoginCredential(whenIWorkToken);
  }, [authData?.loginToken, manageLoginToken, whenIWorkToken]);

  return (
    <NativeBaseProvider>
      <If condition={notificationToastInfo?.show}>
        <Toast {...notificationToastInfo?.details} />
      </If>
      <StackNavigator />
    </NativeBaseProvider>
  );
};

const StackNavigator: React.FC<any> = () => {
  const onboardingState = useAppSelector(gState => gState.onboarding);
  const {fetchDownTimeInfo, downTime} = useFetchDownTimeInfo();
  const user = onboardingState?.currentUser;
  let loginToken = onboardingState?.userAuthData?.loginToken;
  const [isLoggedIn, setIsLoggedIn] = useState(Boolean(loginToken?.length));
  const isB2BOrderFlow = getConfig(WebConfig.b2BRouteEnabled);

  const [isLandscape, setIsLandscape] = useState(false);
  const wasLandscape = useRef(false); // Track previous orientation

  const isMobileOrTablet = useCallback(() => {
    const ua = navigator.userAgent || navigator.vendor;
    return (
      /android|ipad|iphone|ipod/i.test(ua) ||
      (window.innerWidth < 1024 && 'ontouchstart' in window)
    );
  }, []);

  const checkOrientation = useCallback(() => {
    const isLandscapeNow = window.matchMedia(
      '(orientation: landscape)',
    ).matches;
    const isMobile = isMobileOrTablet();
    const isNowLandscapeMobile = isMobile && isLandscapeNow;
    setIsLandscape(isNowLandscapeMobile);
    if (isMobile && !isLandscapeNow && wasLandscape.current) {
      wasLandscape.current = false; // Reset so it doesn't repeat
      const lastPath = localStorage.getItem('lastPath');
      if (lastPath) window.location.replace(lastPath);
      else window.location.reload();
    }
    // Update ref for next check
    wasLandscape.current = isNowLandscapeMobile;
  }, [isMobileOrTablet]);

  useEffect(() => {
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
    };
  }, []);

  useEffect(() => {
    fetchDownTimeInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    gqlService?.getLoginToken();
    gqlService?.setWhenIWorkToken();
  }, []);

  useEffect(() => {
    if (loginToken?.length) setIsLoggedIn(true);
  }, [loginToken?.length]);

  if (isLandscape && isB2BOrderFlow) {
    const fullPath = location.pathname + location.search + location.hash;
    if (location?.search?.length) localStorage.setItem('lastPath', fullPath);
    return <OrientationWarning />;
  }

  if (isB2BOrderFlow) return <StackB2BNavigatorBeforeLogin />;
  else if (downTime) return <StackNavigatorForMaintenance />;
  else if (isLoggedIn && user) return <StackNavigatorAfterLogin />;
  return <StackNavigatorBeforeLogin />;
};

const StackNavigatorForMaintenance: React.FC = () => {
  return (
    <Stack.Navigator key={'Maintenance'} screenOptions={{headerShown: false}}>
      <Stack.Screen name={MainScreens.DownTime} component={DownTime} />
    </Stack.Navigator>
  );
};

const StackB2BNavigatorBeforeLogin: React.FC = () => {
  const {fetchLocalizationData, isCompleted} = useFetchLocalizationData();
  useEffect(() => {
    fetchLocalizationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isCompleted)
    return (
      <Stack.Navigator
        key={'B2BFLow'}
        screenOptions={{headerShown: false, orientation: 'portrait'}}>
        <Stack.Screen name={MainScreens.B2B} component={Splash} />
      </Stack.Navigator>
    );

  return (
    <Stack.Navigator
      key={'B2BFLow'}
      screenOptions={{headerShown: false, orientation: 'portrait'}}>
      <Stack.Screen name={MainScreens.B2B} component={B2BUserNavigator} />
    </Stack.Navigator>
  );
};

const StackNavigatorBeforeLogin: React.FC = () => {
  return (
    <Stack.Navigator key={'BeforeLogin'} screenOptions={{headerShown: false}}>
      <Stack.Screen
        name={MainScreens.HomeBeforeLogin}
        component={Auth.OnboardingNavigator}
      />
      <Stack.Screen name={MainScreens.Common} component={Common} />
      <Stack.Screen name={MainScreens.DownTime} component={DownTime} />
    </Stack.Navigator>
  );
};

const StackNavigatorAfterLogin: React.FC = () => {
  return <FulfillmentStackNavigator />;
};

const navigationOption = {
  gestureEnabled: false,
};

const FulfillmentStackNavigator: React.FC = () => {
  const onboardingState = useAppSelector(gState => gState.onboarding);
  const user = onboardingState?.currentUser;

  const route = getScreenToNavigate(user);

  const [intialRoute] = useState(route.screen);

  return (
    <Stack.Navigator
      screenOptions={{headerShown: false, gestureEnabled: false}}
      key={'Shopper'}
      initialRouteName={intialRoute}>
      <Stack.Group>
        <Stack.Screen name={MainScreens.Hiring} component={HiringNavigator} />
        <Stack.Screen name={MainScreens.Tabs} component={BottomTab} />
        <Stack.Screen name={MainScreens.Home} component={Home.HomeNavigator} />
        <Stack.Screen name={MainScreens.Lockout} component={Lockout} />
        <Stack.Screen
          name={HomeScreens.B2B}
          component={B2BNavigator}
          // options={navigationOption}
        />
      </Stack.Group>
      <Stack.Screen name={MainScreens.Common} component={Common} />
      <Stack.Screen
        name={MainScreens.Driver}
        component={DriverNavigator}
        options={navigationOption}
      />

      {/* For Deeplink only */}
      <Stack.Screen name={MainScreens.Shopper} component={ShopperNavigator} />
      <Stack.Screen name={MainScreens.DownTime} component={DownTime} />
    </Stack.Navigator>
  );
};

export default MainNavigator;
